import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { onboardingApi, aiApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Network, ArrowRight, ArrowLeft, Loader2, Sparkles, Check, Code, ShoppingCart, TrendingUp, Building2, Heart, Briefcase, Factory, Film, Bitcoin, Zap } from 'lucide-react';

const iconMap = {
  Code, ShoppingCart, TrendingUp, Building2, Heart, Briefcase, Factory, Film, Bitcoin, Zap
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [formData, setFormData] = useState({
    industry: '',
    business_description: '',
    annual_revenue: '',
    employee_count: '',
    primary_markets: [],
    goals: []
  });

  useEffect(() => {
    if (user?.onboarding_completed) {
      navigate('/dashboard');
      return;
    }
    loadIndustries();
  }, [user, navigate]);

  const loadIndustries = async () => {
    try {
      const res = await onboardingApi.getIndustries();
      setIndustries(res.data.industries);
    } catch (err) {
      console.error('Failed to load industries:', err);
    }
  };

  const goals = [
    { id: 'tax_optimization', label: 'Tax Optimization' },
    { id: 'asset_protection', label: 'Asset Protection' },
    { id: 'international_expansion', label: 'International Expansion' },
    { id: 'privacy', label: 'Privacy & Confidentiality' },
    { id: 'succession_planning', label: 'Succession Planning' },
    { id: 'ip_protection', label: 'IP Protection' }
  ];

  const revenues = [
    { id: 'under_100k', label: 'Under $100K' },
    { id: '100k_500k', label: '$100K - $500K' },
    { id: '500k_1m', label: '$500K - $1M' },
    { id: '1m_5m', label: '$1M - $5M' },
    { id: '5m_plus', label: '$5M+' }
  ];

  const toggleGoal = (goalId) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.includes(goalId)
        ? prev.goals.filter(g => g !== goalId)
        : [...prev.goals, goalId]
    }));
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await onboardingApi.complete(formData);
      
      setAnalyzing(true);
      // Trigger AI analysis
      await aiApi.analyzeJurisdiction({
        industry: formData.industry,
        business_description: formData.business_description,
        goals: formData.goals,
        annual_revenue: formData.annual_revenue
      });
      
      updateUser({ ...user, onboarding_completed: true, industry: formData.industry });
      navigate('/dashboard');
    } catch (err) {
      console.error('Onboarding failed:', err);
    } finally {
      setLoading(false);
      setAnalyzing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="onboarding-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/corpcraft-logo.png" 
              alt="CorpCraft Logo" 
              className="h-10 w-auto object-contain"
            />
          </div>
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-3 h-3 rounded-full transition-colors ${
                  s <= step ? 'bg-blue-500' : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Step 1: Industry Selection */}
        {step === 1 && (
          <div className="animate-fade-in space-y-8" data-testid="onboarding-step-1">
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold font-['Outfit']">What industry is your business in?</h1>
              <p className="text-slate-400 text-lg">This helps us recommend the optimal jurisdiction and structure</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {industries.map((ind) => {
                const Icon = iconMap[ind.icon] || Briefcase;
                return (
                  <button
                    key={ind.id}
                    onClick={() => setFormData({ ...formData, industry: ind.id })}
                    className={`industry-card text-left ${
                      formData.industry === ind.id ? 'selected' : ''
                    }`}
                    data-testid={`industry-${ind.id}`}
                  >
                    <Icon className={`w-8 h-8 mb-3 ${
                      formData.industry === ind.id ? 'text-blue-400' : 'text-slate-400'
                    }`} />
                    <div className="font-medium">{ind.name}</div>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => setStep(2)}
                disabled={!formData.industry}
                className="gradient-accent hover:opacity-90 h-12 px-8"
                data-testid="onboarding-next-1"
              >
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Business Details */}
        {step === 2 && (
          <div className="animate-fade-in space-y-8" data-testid="onboarding-step-2">
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold font-['Outfit']">Tell us about your business</h1>
              <p className="text-slate-400 text-lg">Help our AI understand your specific needs</p>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-slate-300">Business Description</Label>
                <Textarea
                  value={formData.business_description}
                  onChange={(e) => setFormData({ ...formData, business_description: e.target.value })}
                  placeholder="Describe what your business does, your target market, and key operations..."
                  className="bg-slate-900 border-slate-800 focus:border-blue-500 min-h-[120px]"
                  data-testid="business-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Annual Revenue</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {revenues.map((rev) => (
                    <button
                      key={rev.id}
                      onClick={() => setFormData({ ...formData, annual_revenue: rev.id })}
                      className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                        formData.annual_revenue === rev.id
                          ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                          : 'border-slate-800 bg-slate-900 text-slate-400 hover:border-slate-700'
                      }`}
                      data-testid={`revenue-${rev.id}`}
                    >
                      {rev.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!formData.business_description || !formData.annual_revenue}
                className="gradient-accent hover:opacity-90 h-12 px-8"
                data-testid="onboarding-next-2"
              >
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Goals */}
        {step === 3 && (
          <div className="animate-fade-in space-y-8" data-testid="onboarding-step-3">
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold font-['Outfit']">What are your goals?</h1>
              <p className="text-slate-400 text-lg">Select all that apply to optimize your structure</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {goals.map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => toggleGoal(goal.id)}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    formData.goals.includes(goal.id)
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-slate-800 bg-slate-900 hover:border-slate-700'
                  }`}
                  data-testid={`goal-${goal.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                      formData.goals.includes(goal.id)
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-slate-600'
                    }`}>
                      {formData.goals.includes(goal.id) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className={formData.goals.includes(goal.id) ? 'text-white' : 'text-slate-300'}>
                      {goal.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-between">
              <Button
                variant="ghost"
                onClick={() => setStep(2)}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleComplete}
                disabled={loading || formData.goals.length === 0}
                className="gradient-accent hover:opacity-90 h-12 px-8"
                data-testid="onboarding-complete-btn"
              >
                {loading ? (
                  <>
                    {analyzing ? (
                      <>
                        <Sparkles className="w-5 h-5 mr-2 animate-pulse" />
                        AI Analyzing...
                      </>
                    ) : (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    )}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Structure
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
